import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

interface UnsavedChangesHandler {
  isDirty: boolean;
  onAttemptNavigate: (continueNavigation: () => void) => void;
}

interface UnsavedChangesGuardContextValue {
  registerHandler: (handler: UnsavedChangesHandler | null) => void;
  guardNavigation: (action: () => void) => boolean;
}

const UnsavedChangesGuardContext = createContext<UnsavedChangesGuardContextValue | null>(null);

export const UnsavedChangesGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handlerRef = useRef<UnsavedChangesHandler | null>(null);

  const registerHandler = useCallback((handler: UnsavedChangesHandler | null) => {
    handlerRef.current = handler;
  }, []);

  const guardNavigation = useCallback((action: () => void): boolean => {
    const handler = handlerRef.current;
    if (handler?.isDirty) {
      handler.onAttemptNavigate(action);
      return false;
    }
    action();
    return true;
  }, []);

  const value = useMemo(
    () => ({
      registerHandler,
      guardNavigation,
    }),
    [registerHandler, guardNavigation],
  );

  return (
    <UnsavedChangesGuardContext.Provider value={value}>
      {children}
    </UnsavedChangesGuardContext.Provider>
  );
};

export const useUnsavedChangesGuard = (): UnsavedChangesGuardContextValue => {
  const context = useContext(UnsavedChangesGuardContext);
  if (!context) {
    throw new Error('useUnsavedChangesGuard must be used within UnsavedChangesGuardProvider');
  }
  return context;
};
